import React from "react";
import LegalLayout, { H2, P, UL } from "@/components/LegalLayout";

const H3 = ({ children }) => (
  <h3 className="font-display font-semibold text-base mt-6 tracking-tight text-foreground">{children}</h3>
);

export default function Refunds() {
  return (
    <LegalLayout title="Refund Policy" updated="February 24, 2026">
      <P>Thank you for purchasing a Product from Paddle.</P>
      <P>
        Paddle acts as the authorised reseller for purchases of software and other digital products created by thousands of software developers (<strong>“Suppliers”</strong>).
      </P>
      <P>
        This Policy details when you may be entitled to withdraw a Transaction and/or receive a refund, and how to request one. It applies to Transactions completed by Consumers and Businesses.
      </P>
      <P>
        If local consumer protection laws or a Supplier of a Product provides you with additional or non-waivable rights, the highest level of rights will always apply. Nothing in this Policy limits your mandatory consumer rights.
      </P>
      <P>
        For any questions about this Policy or to request a refund, please visit our Buyer support site at{" "}
        <a className="text-primary underline" href="https://paddle.net" target="_blank" rel="noreferrer">https://paddle.net</a>.
      </P>
      <P>
        This Policy forms part of Paddle Buyer Terms and Conditions, and is subject to their terms and definitions. If there is any inconsistency, Paddle Buyer Terms and Conditions will apply.
      </P>

      <H2>1. Global Refund Policy</H2>
      <UL>
        <li>1.1. Unless required by applicable law, all Transactions are non-refundable and non-exchangeable.</li>
        <li>1.2. Paddle may issue refunds on a discretionary basis (see section 7 below) or if you exercise an applicable statutory withdrawal or refund right (see section 2 below).</li>
        <li>1.3. Refunds will not be issued where there is evidence of fraud, refund abuse, or other manipulative behaviour.</li>
        <li>1.4. This Policy does not affect consumer rights in relation to Products which are not as described, faulty or not fit for purpose (see section 4 below).</li>
        <li>1.5. Refund requests must be made within the applicable statutory or discretionary period described below.</li>
        <li>1.6. If you receive a refund in accordance with this Policy, access to the relevant Product will cease.</li>
      </UL>

      <H2>2. Country-Specific Rules</H2>
      <P>
        2.1. Where local consumer protection laws grant unconditional “withdrawal” rights, those rights apply and override this Policy and any Supplier policy. Where regional differences apply, Paddle applies the highest standard of protection across the relevant country, which is reflected below.
      </P>

      <H3>2.2. European Union / EEA / Switzerland / United Kingdom</H3>
      <UL>
        <li>2.2.1. Consumers have a 14-day statutory right to withdraw from some digital content and service contracts and receive a full refund.</li>
        <li>2.2.2. The right to withdraw applies to one-off purchases and to the first payment under a Subscription contract. It does not apply to subsequent Subscription payments, except in the circumstances described in section 2.2.3 below. If a Subscription includes a free trial period, then, as soon as that free trial period ends, you will again have a period of 14 calendar days to exercise your right to withdraw.</li>
        <li>2.2.3. If you completed a Transaction in the UK and have an annual Subscription, you will have a new period of 14 calendar days to exercise your right to withdraw starting the day the Subscription auto-renews for another year.</li>
        <li>2.2.4. The right to withdraw does not apply to the supply of digital content Products that have started to be downloaded, streamed or otherwise used, when you have given express consent to waive your withdrawal rights.</li>
        <li>2.2.5. To exercise this right, you must request a refund within 14 days from the date of the Transaction (see section 3 below).</li>
      </UL>

      <H3>2.3. Turkey / Israel</H3>
      <UL>
        <li>2.3.1. Consumers have a 14-day statutory right to withdraw from some digital content and service contracts and receive a full refund.</li>
        <li>2.3.2. To exercise this right, you must request a refund within 14 days from the date of the Transaction (see section 3 below).</li>
      </UL>

      <H3>2.4. South Korea / Brazil / China</H3>
      <UL>
        <li>2.4.1. Consumers have a 7-day unconditional right to cancel digital content or service contracts after delivery and receive a full refund.</li>
        <li>2.4.2. To exercise this right, you must request a refund within 7 days from the date of the Transaction (see section 3 below).</li>
      </UL>

      <H3>2.5. Canada</H3>
      <UL>
        <li>2.5.1. Consumers have a 7-day unconditional right to cancel digital content or service contracts after delivery and receive a full refund.</li>
        <li>2.5.2. To exercise this right, you must request a refund within 7 days from the date of the Transaction (see section 3 below).</li>
        <li>2.5.3. You acknowledge having received access to a French version of this Policy. Vous reconnaissez avoir reçu une version française de cette politique et avoir demandé.</li>
      </UL>

      <H3>2.6. Singapore</H3>
      <UL>
        <li>2.6.1. Consumers have a 5-day unconditional right to cancel digital content or services after delivery or first access and receive a full refund.</li>
        <li>2.6.2. To exercise this right, you must request a refund within 5 days from the date of the Transaction (see section 3 below).</li>
      </UL>

      <H2>3. How to Withdraw and Request a Refund</H2>
      <P>3.1. To withdraw, cancel and/or request a refund, contact Paddle using one of the following methods:</P>
      <UL>
        <li>use the “View receipt” or “Manage subscription” link in your Transaction confirmation email;</li>
        <li>submit a request via the support link provided in your receipt or within your account’s billing page; or</li>
        <li>
          visit{" "}
          <a className="text-primary underline" href="https://paddle.net" target="_blank" rel="noreferrer">paddle.net</a>{" "}
          and select the “Request refund” option.
        </li>
      </UL>
      <P>3.2. If eligible, refunds will be processed using the same payment method where possible and within 14 days of approval of the request.</P>
      <P>3.3. Paddle’s transaction records will be used to verify eligibility and timing but will not override your statutory rights.</P>
      <P>
        3.4. If you are not sure of the details of your Transaction or whether you are eligible for a refund, please contact us{" "}
        <a className="text-primary underline" href="https://paddle.net" target="_blank" rel="noreferrer">here</a> and we will do our best to help.
      </P>
      <P>3.5. If a transaction is not eligible for a refund, you may still cancel the subscription at any time to prevent future billing. The cancellation will take effect at the end of the billing period of your subscription, and will prevent any further payments from being taken.</P>

      <H2>4. Refunds for Technical or Product Defects</H2>
      <P>If you experience persistent technical issues with the Product you purchased using the Services or a material defect that prevents you from accessing the features or benefits as described, please:</P>
      <UL>
        <li>contact the Supplier first to attempt to resolve the issue or request a refund;</li>
        <li>if the issue cannot be resolved, contact Paddle’s support team (see section 3 above) and provide details of the issue and any response received from the Supplier;</li>
        <li>where there is evidence of a material technical or Product defect, Paddle will issue a refund in accordance with applicable consumer protection laws.</li>
      </UL>

      <H2>5. Add-Ons and One-Time Transactions</H2>
      <UL>
        <li>5.1. Add-ons linked to a main Subscription expire when the main Subscription ends, unless otherwise stated.</li>
        <li>5.2. Refund eligibility for add-ons and one-time Transactions follows the same criteria as the main Transaction, unless local law provides otherwise.</li>
        <li>5.3. Items that are delivered and fully accessible immediately may be non-refundable once delivered, except where required by law.</li>
      </UL>

      <H2>6. Chargebacks and Payment Disputes</H2>
      <UL>
        <li>6.1. We encourage you to contact Paddle prior to raising a request for a chargeback or raising a dispute with your bank, card issuer or other payment provider.</li>
        <li>6.2. If you initiate a chargeback or payment reversal, access to the relevant Product may be temporarily suspended while the matter is reviewed.</li>
        <li>6.3. On receipt of the chargeback or dispute, Paddle will provide the payment provider with payment details and, where relevant, your consent to waive statutory rights.</li>
        <li>6.4. This does not affect your lawful rights to dispute a charge under card-scheme or consumer-protection rules.</li>
      </UL>

      <H2>7. Discretionary Refunds</H2>
      <UL>
        <li>7.1. Paddle may, at its sole discretion, issue a refund if a request is submitted within 14 days of your Transaction date. Submission of a request within this 14-day period does not guarantee a refund.</li>
        <li>7.2. All refund requests are reviewed on a case-by-case basis. Paddle may consider relevant factors including the nature of the Product, the reason for the request, usage or consumption, and any applicable contractual terms. Paddle may approve a refund in full, approve a partial refund, or decline the request.</li>
        <li>7.3. Any discretionary refund granted by Paddle is voluntary and does not create an obligation to provide refunds in the future, including for similar requests. Paddle’s decision to issue (or not issue) a refund does not waive any rights or remedies Paddle may have under applicable agreements or law.</li>
      </UL>

      <H2>8. Updates to this Policy</H2>
      <UL>
        <li>8.1. Paddle may update this Policy from time to time.</li>
        <li>8.2. The version in effect at the time of your Transaction governs that transaction. We recommend saving or printing a copy for your records.</li>
      </UL>

      <H2>9. Governing Law and Resolving Disputes</H2>
      <P>This Policy is subject to the governing law, complaints and disputes provisions set out in the Paddle Buyer Terms and Conditions.</P>
    </LegalLayout>
  );
}
